import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Linkedin, ArrowRight } from "lucide-react";
import { api, ACCENTS } from "@/lib/api";
import VentureLogo from "@/components/VentureLogo";
import { Reveal, SectionHeading, StatusBadge } from "./Section";

const ZEPZYO_PHASES = [
  ["Phase 1", "Grassroots Healthcare Network", "2026–2027", true],
  ["Phase 2", "Build-Up & Care Centres", "2027–2028", false],
  ["Phase 3", "AI Clinical Assistant", "2028–2029", false],
  ["Phase 4", "Continuous Clinical Intelligence", "2029–2030", false],
  ["Phase 5", "Intelligent Care Network", "2030 onward", false],
  ["Phase 6", "Intelligent Healthcare at Scale", "Vision", false],
];

const VentureCard = ({ v, large = false, delay = 0 }) => {
  const a = ACCENTS[v.accent] || ACCENTS.gold;
  return (
    <Reveal delay={delay} className="h-full">
      <article className={`group relative h-full rounded-2xl border border-white/8 bg-[#10141d] p-7 overflow-hidden hover:border-[#E2C08D]/25 transition-colors duration-500 ${large ? "md:p-10" : ""}`} data-testid={`venture-card-${v.slug}`}>
        <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full opacity-[0.07] blur-3xl transition-opacity duration-700 group-hover:opacity-[0.16]" style={{ background: a.dot }} />
        <div className="flex items-start justify-between gap-4">
          <VentureLogo v={v} size={large ? "h-20 w-20" : "h-14 w-14"} />
          <StatusBadge accent={v.accent}>{v.status}</StatusBadge>
        </div>
        <h3 className={`mt-6 font-display font-semibold text-slate-100 ${large ? "text-3xl md:text-4xl" : "text-2xl"}`}>{v.name}</h3>
        {v.role && <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{v.role}</p>}
        <p className={`mt-2 font-display italic ${large ? "text-xl" : "text-lg"} ${a.text}`}>{v.tagline}</p>
        <p className="mt-4 text-sm text-slate-400 leading-relaxed line-clamp-4">{v.description}</p>
        {v.highlights?.length > 0 && (
          <ul className="mt-5 space-y-1.5">
            {v.highlights.slice(0, large ? 4 : 3).map((h) => (
              <li key={h} className="flex items-start gap-2 text-[13px] text-slate-300">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full" style={{ background: a.dot }} /> {h}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/venture/${v.slug}`} data-testid={`venture-explore-${v.slug}`} className="group/link inline-flex items-center gap-1.5 rounded-full bg-[#E2C08D]/12 border border-[#E2C08D]/30 px-4 py-2 text-xs font-semibold text-[#E2C08D] hover:bg-[#E2C08D] hover:text-[#0A0C10] transition-colors">
            Explore <ArrowRight size={12} className="transition-transform group-hover/link:translate-x-0.5" />
          </Link>
          {v.website && (
            <a href={v.website} target="_blank" rel="noopener noreferrer" data-testid={`venture-website-${v.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-200 hover:border-[#E2C08D]/50 hover:text-[#E2C08D] transition-colors">
              Visit Website <ExternalLink size={12} />
            </a>
          )}
          {v.linkedin && (
            <a href={v.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`venture-linkedin-${v.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-200 hover:border-[#38BDF8]/50 hover:text-[#38BDF8] transition-colors">
              LinkedIn <Linkedin size={12} />
            </a>
          )}
        </div>
      </article>
    </Reveal>
  );
};

const Ventures = () => {
  const [ventures, setVentures] = useState([]);
  useEffect(() => {
    api.get("/ventures").then((r) => setVentures(r.data)).catch(() => {});
  }, []);

  const featured = ventures.filter((v) => v.featured);
  const main = ventures.filter((v) => v.kind === "venture" && !v.featured);
  const professional = ventures.filter((v) => v.kind === "professional");
  const community = ventures.filter((v) => v.kind === "community");

  return (
    <section id="ventures" className="relative py-28 md:py-36" data-testid="ventures-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading index="04" eyebrow="Ventures & Companies" title="Building beyond one company." subtitle="Every venture below is a living story — aviation, railways, healthcare, agriculture, climate and community. Select any card to explore its full story." />
        <div className="grid gap-5 md:grid-cols-2">
          {featured.map((v) => <div key={v.id} className="md:col-span-2"><VentureCard v={v} large /></div>)}
          {main.map((v, i) => <VentureCard key={v.id} v={v} delay={(i % 2) * 0.08} />)}
        </div>

        <Reveal>
          <div className="mt-20 rounded-2xl border border-[#FB7185]/20 bg-gradient-to-br from-[#141019] to-[#10141d] p-8 md:p-12" data-testid="zepzyo-vision">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-10">
              <h3 className="font-display text-3xl font-semibold text-slate-100">ZEPZYO — <span className="italic text-[#FB7185]">the six-phase vision</span></h3>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">“Every person deserves access to better healthcare.”</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
              {ZEPZYO_PHASES.map(([phase, name, years, active], i) => (
                <Reveal key={phase} delay={i * 0.06}>
                  <div className={`h-full rounded-xl border p-4 ${active ? "border-[#FB7185]/50 bg-[#FB7185]/10" : "border-white/8 bg-white/[0.02]"}`} data-testid={`zepzyo-${phase.toLowerCase().replace(" ", "-")}`}>
                    <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${active ? "text-[#FB7185]" : "text-slate-500"}`}>{phase}</div>
                    <div className="mt-2 text-sm font-semibold text-slate-200 leading-snug">{name}</div>
                    <div className="mt-2 font-mono text-[11px] text-slate-500">{years}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>

        {(professional.length > 0 || community.length > 0) && (
          <div className="mt-20">
            <Reveal><h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-200 mb-8">Professional chapters & community</h3></Reveal>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[...professional, ...community].map((v, i) => (
                <Reveal key={v.id} delay={i * 0.06}>
                  <Link to={`/venture/${v.slug}`} className="flex h-full flex-col items-center rounded-xl border border-white/8 bg-[#10141d] p-6 text-center hover:border-[#E9C88A]/40 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(233,200,138,0.1)] transition-all duration-500" data-testid={`chapter-card-${v.slug}`}>
                    <VentureLogo v={v} size="h-14 w-14" />
                    <h4 className="mt-4 font-semibold text-slate-100">{v.name}</h4>
                    <p className="mt-1 text-xs text-slate-500">{v.status}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Ventures;
